import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';
import { Logo } from './Logo';
import { buttonClass } from './styles';

function Fallback({ title, message, onRetry }: { title: string; message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="mx-auto my-16 max-w-md text-center">
      <h1 className="text-2xl font-semibold tracking-[-0.02em]">{title}</h1>
      <p className="mt-2 text-sm text-muted-ink">{message}</p>
      <div className="mt-6 flex justify-center gap-2">
        {onRetry && (
          <button type="button" className={buttonClass('primary')} onClick={onRetry}>
            Try again
          </button>
        )}
        <Link to="/" className={buttonClass('secondary')}>
          Go to the dashboard
        </Link>
      </div>
    </div>
  );
}

/** Catches render errors in one screen so the rest of the app keeps working. */
export class RouteErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Screen crashed', error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <Fallback
          title="This screen hit a snag"
          message="Something went wrong while showing this page. Your data is safe."
          onRetry={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}

/** Router-level errorElement (also covers loader / lazy-import failures). */
export function RouteError() {
  const error = useRouteError();
  const notFound = isRouteErrorResponse(error) && error.status === 404;
  return (
    <div className="min-h-dvh px-4 py-10">
      <div className="flex justify-center">
        <Logo className="h-8" />
      </div>
      <Fallback
        title={notFound ? 'Page not found' : 'Something went wrong'}
        message={
          notFound
            ? 'That page doesn’t exist.'
            : 'MyDash couldn’t load this page. Reload, or try again in a moment.'
        }
        onRetry={notFound ? undefined : () => window.location.reload()}
      />
    </div>
  );
}
